import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';

export const GRUPOS_ICONOS: { label: string; iconos: string[] }[] = [
  { label: 'Finanzas',       iconos: ['💰','💵','💴','💶','💷','💳','🏦','🏧','📈','📉','💹','🪙','💎','🏆','🎯','💼','📊','🤑'] },
  { label: 'Alimentación',   iconos: ['🍔','🍕','🍣','🌮','🥗','🍱','☕','🍺','🍷','🥂','🛒','🧾','🍎','🥦','🧁','🍰'] },
  { label: 'Transporte',     iconos: ['🚗','🚕','🚌','🚇','🚂','✈️','🚲','🛵','🛺','⛽','🅿️','🗺️','🚙','🛻','🚐','⚓'] },
  { label: 'Hogar',          iconos: ['🏠','🏡','🏢','💡','🔧','🪛','🛋️','🧹','🪣','🔑','🚪','🪴','🛁','🧺','🧴','🪟'] },
  { label: 'Salud',          iconos: ['🏥','💊','🩺','🩹','🧬','🦷','👓','🩻','🏋️','🧘','🚴','🤸','💆','🩴','🌡️','😷'] },
  { label: 'Entretenimiento',iconos: ['🎬','🎮','🎵','🎸','🎧','📺','🎭','🎨','🎯','🎲','🃏','🎱','🎤','📷','📸','🎪'] },
  { label: 'Ropa y personal',iconos: ['👔','👗','👟','👠','💄','💈','🛍️','⌚','💍','👒','🕶️','🧢','🧣','🧤','👜','🪞'] },
  { label: 'Tecnología',     iconos: ['📱','💻','🖥️','⌨️','🖱️','📷','🎧','🖨️','📡','🔋','💾','📀','🖲️','📟','📠','🔌'] },
  { label: 'Educación',      iconos: ['🎓','📚','📖','✏️','📝','🔬','🔭','🌍','🏫','📐','📏','🖊️','📓','🗒️','📌','🔖'] },
  { label: 'Viajes',         iconos: ['✈️','🏖️','🏝️','⛺','🗺️','🧳','🏔️','🗼','🏯','🌅','🌄','🗽','🏕️','🛫','🛬','🚢'] },
  { label: 'Mascotas',       iconos: ['🐶','🐱','🐹','🐰','🦊','🐦','🐠','🐢','🦴','🐾','🐮','🐷','🦄','🐴','🐑','🐓'] },
  { label: 'Familia/Social', iconos: ['👨‍👩‍👧','👶','🎂','🎁','🎉','💒','🤝','❤️','💌','🎀','🪆','🧸','🎠','🎡','🎢','🎊'] },
  { label: 'Otros',          iconos: ['⚡','💧','🌱','🌿','☀️','🌙','⭐','❄️','🔥','💫','✨','🌈','🍀','🎋','🪐','♻️'] },
];

// Lista plana para búsqueda
const TODOS = GRUPOS_ICONOS.flatMap(g => g.iconos);

interface IconPickerProps {
  value: string;
  onChange: (icono: string) => void;
  grupos?: typeof GRUPOS_ICONOS;
}

export const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, grupos = GRUPOS_ICONOS }) => {
  const [busqueda, setBusqueda] = useState('');
  const [grupoActivo, setGrupoActivo] = useState<string | null>(null);

  const iconosFiltrados = useMemo(() => {
    if (busqueda.trim()) {
      // Filtra todos los iconos que contengan el término buscado en el nombre de su grupo
      const term = busqueda.toLowerCase();
      const porNombreGrupo = grupos
        .filter(g => g.label.toLowerCase().includes(term))
        .flatMap(g => g.iconos);
      // Si no hay coincidencias por grupo, muestra todos
      return porNombreGrupo.length ? porNombreGrupo : TODOS;
    }
    if (grupoActivo) {
      return grupos.find(g => g.label === grupoActivo)?.iconos ?? [];
    }
    return null; // null = mostrar todos los grupos
  }, [busqueda, grupoActivo, grupos]);

  return (
    <div className="space-y-2">
      {/* Vista previa + buscador */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-2xl shrink-0">
          {value}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={busqueda}
            onChange={e => { setBusqueda(e.target.value); setGrupoActivo(null); }}
            placeholder="Buscar por categoría…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Tabs de grupos (solo cuando no hay búsqueda) */}
      {!busqueda && (
        <div className="flex flex-wrap gap-1.5">
          <button type="button"
            onClick={() => setGrupoActivo(null)}
            className={`px-2 py-1 text-xs rounded-lg font-semibold transition ${
              grupoActivo === null
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}>
            Todos
          </button>
          {grupos.map(g => (
            <button key={g.label} type="button"
              onClick={() => setGrupoActivo(g.label)}
              className={`px-2 py-1 text-xs rounded-lg font-semibold transition ${
                grupoActivo === g.label
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}>
              {g.label}
            </button>
          ))}
        </div>
      )}

      {/* Grid de iconos */}
      <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 p-2 bg-gray-50 dark:bg-gray-700/50">
        {iconosFiltrados === null ? (
          // Todos los grupos
          grupos.map(g => (
            <div key={g.label} className="mb-3 last:mb-0">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 px-1">{g.label}</p>
              <div className="grid grid-cols-9 gap-1">
                {g.iconos.map(ic => (
                  <button key={ic} type="button" onClick={() => onChange(ic)}
                    className={`text-lg p-1.5 rounded-lg transition hover:bg-gray-200 dark:hover:bg-gray-600 ${
                      ic === value ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500' : ''
                    }`}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="grid grid-cols-9 gap-1">
            {iconosFiltrados.map(ic => (
              <button key={ic} type="button" onClick={() => onChange(ic)}
                className={`text-lg p-1.5 rounded-lg transition hover:bg-gray-200 dark:hover:bg-gray-600 ${
                  ic === value ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500' : ''
                }`}>
                {ic}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
